import { Component, computed, effect, signal, DestroyRef, inject } from "@angular/core";
import { LayerService } from "../layer.service";
import { InfoTextService } from "@services/info-text.service";
import { LayerInfo } from "src/app/shared/contract/Layer/LayerOverview";
import { getColorRampFromLayerLegendElements } from "src/app/shared/helpers/color-ramp-helper";
import { MapContainerService } from "src/app/shared/services/map-container.service";
import { LayerLegendResponse } from "src/app/shared/contract/Layer/LayerLegend";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { InterfaceStatus } from "src/app/shared/contract/Enum/Status";
import { LayerApiService } from "./layer-api.service";
import { SmallDialogContainerComponent } from "@components/small-dialog-container/small-dialog-container.component";
import { MatIcon } from "@angular/material/icon";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { FormsModule } from "@angular/forms";
import { InfoExpansionPanelComponent } from "@components/info-expansion-panel/info-expansion-panel.component";
import { NgOptimizedImage, KeyValuePipe } from "@angular/common";
import { ColorRampComponent } from "@components/color-ramp/color-ramp.component";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LoadingService } from "../../loading/loading.service";
import { finalize } from "rxjs";

@Component({
  selector: "app-legend",
  templateUrl: "./legend.component.html",
  styleUrls: ["./legend.component.scss"],
  imports: [
    SmallDialogContainerComponent,
    MatIcon,
    MatSlideToggle,
    FormsModule,
    InfoExpansionPanelComponent,
    NgOptimizedImage,
    ColorRampComponent,
    KeyValuePipe,
    TranslocoPipe
  ]
})
export class LegendComponent {
  layerService = inject(LayerService);
  private infoTextService = inject(InfoTextService);
  private loadingService = inject(LoadingService);
  private mapContainerService = inject(MapContainerService);
  private layerApiService = inject(LayerApiService);
  private destroyRef = inject(DestroyRef);
  private translocoService = inject(TranslocoService);

  getColorRamp = getColorRampFromLayerLegendElements;

  protected legends = signal<Map<number, LayerLegendResponse & { displayName: string }>>(new Map());

  constructor() {
    effect(() => {
      const layerInfos = this.layerService.activeLayers();
      this.setLegends(layerInfos);
    });

    this.translocoService.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.legends.set(new Map());
      const layerInfos = this.layerService.activeLayers();
      this.setLegends(layerInfos);
    });
  }

  legendPosition = computed(() => {
    const menuWidth = 56;
    const componentConfig = this.mapContainerService.leftComponentConfig();
    if (componentConfig.alignment() !== "left") {
      return menuWidth + 24;
    }
    return componentConfig.currentWidth() + menuWidth + 24; // 24 is the margin of the legend container
  });

  setLegends(layerInfos: LayerInfo[]) {
    // Get current legends
    const currentLegends = this.legends();
    const newLegends = new Map(currentLegends);

    // Track active layer IDs
    const activeLayerIds = new Set<number>();

    // Add new legends for active layers
    layerInfos.forEach(layerInfo => {
      if (layerInfo.IsActivated) {
        activeLayerIds.add(layerInfo.Id);

        // Only load if we don't already have this legend
        if (!currentLegends.has(layerInfo.Id)) {
          this.loadLegendForLayer(layerInfo.Id);
        }
      }
    });

    // Remove legends for inactive layers
    currentLegends.forEach((_, layerId) => {
      if (!activeLayerIds.has(layerId)) {
        newLegends.delete(layerId);
      }
    });

    // Update legends signal if changes were made
    if (newLegends.size !== currentLegends.size) {
      this.legends.set(newLegends);
    }
  }

  private loadLegendForLayer(layerId: number): void {
    this.loadingService.show();
    this.layerApiService
      .getLayerLegend(layerId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loadingService.hide())
      )
      .subscribe(legend => {
        if (legend.Status === InterfaceStatus.OK) {
          this.addOrUpdateLegend(legend);
        }
      });
  }

  public addOrUpdateLegend(legend: LayerLegendResponse): void {
    if (legend.Status !== InterfaceStatus.OK) return;

    const currentCache = this.legends();
    const newCache = new Map(currentCache);
    newCache.set(legend.LayerId, { ...legend, displayName: this.getDisplayNameForLayer(legend.LayerId) });

    this.legends.set(newCache);
  }

  hideLegend() {
    this.layerService.showLegend.set(false);
  }

  ActivateBorder() {
    this.layerService.showDistrict.set(!this.layerService.showDistrict());
  }

  private getLayerInfoById(layerId: number): LayerInfo | undefined {
    return this.layerService
      .layerGroups()
      .flatMap(group => group.LayerInfos)
      .find(layer => layer.Id === layerId);
  }

  private getDisplayNameForLayer(layerId: number): string {
    const layerInfo = this.getLayerInfoById(layerId);
    if (!layerInfo?.DisplayName) return "";

    return this.infoTextService.getInfoTextByLocale(layerInfo.DisplayName)[0].Text;
  }
}
